import { ArrowLeft, AtSign, Check, LoaderCircle, LockKeyhole, Mail, X } from 'lucide-react'
import { type FormEvent, useEffect, useRef, useState } from 'react'
import { profileIdentitySetupNotice } from './featureAvailability'

export type AuthMember = {
  id: string
  handle: string
  display_name: string
  displayName?: string
  avatar_seed?: string | null
  avatar_url?: string | null
  avatarUrl?: string | null
  avatar_version?: number | string | null
  bio?: string | null
  role?: string
  is_verified?: boolean
  is_demo?: boolean
}

type AuthStep = 'email' | 'code' | 'profile'

type StartResponse = {
  challenge_id?: string
  expires_in_seconds?: number
  resend_after_seconds?: number
  retryAfterSeconds?: number
  message?: string
}

type VerifyResponse = {
  member?: AuthMember
  needs_profile?: boolean
  needsProfile?: boolean
  needsProfileSetup?: boolean
}

type AuthDialogProps = {
  open: boolean
  onClose: () => void
  onAuthenticated: (member: AuthMember) => void
  onProfilePending: (member: AuthMember) => void
  resumeProfileMember?: AuthMember | null
}

class AuthRequestError extends Error {
  readonly code: string

  constructor(message: string, code = 'auth_error') {
    super(message)
    this.name = 'AuthRequestError'
    this.code = code
  }
}

async function authRequest<T>(path: string, body: Record<string, unknown>): Promise<T> {
  const response = await fetch(path, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  })
  const payload = await response.json().catch(() => ({})) as T & { error?: string; message?: string }
  if (!response.ok) {
    throw new AuthRequestError(payload.message || 'Journey90 could not complete that request.', payload.error)
  }
  return payload
}

function maskedEmail(email: string) {
  const [local = '', domain = ''] = email.split('@')
  if (!domain) return email
  const visible = local.slice(0, Math.min(2, local.length))
  return `${visible}${'•'.repeat(Math.max(3, local.length - visible.length))}@${domain}`
}

function cleanCode(value: string) {
  return value.replace(/\D/g, '').slice(0, 6)
}

function cleanHandle(value: string) {
  return value
    .replace(/^@/, '')
    .toLowerCase()
    .replace(/[^a-z0-9_]/g, '')
    .replace(/^[^a-z0-9]+/, '')
    .slice(0, 30)
}

export default function AuthDialog({ open, onClose, onAuthenticated, onProfilePending, resumeProfileMember = null }: AuthDialogProps) {
  const [step, setStep] = useState<AuthStep>('email')
  const [email, setEmail] = useState('')
  const [challengeId, setChallengeId] = useState('')
  const [code, setCode] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [handle, setHandle] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [resendSeconds, setResendSeconds] = useState(0)
  const pendingMember = useRef<AuthMember | null>(null)
  const dialogRef = useRef<HTMLElement>(null)
  const previouslyFocused = useRef<HTMLElement | null>(null)
  const busyRef = useRef(false)
  const closeRef = useRef(onClose)
  const emailInput = useRef<HTMLInputElement>(null)
  const codeInput = useRef<HTMLInputElement>(null)

  useEffect(() => {
    busyRef.current = busy
  }, [busy])

  useEffect(() => {
    closeRef.current = onClose
  }, [onClose])

  useEffect(() => {
    if (open && resumeProfileMember) {
      pendingMember.current = resumeProfileMember
      setStep('profile')
      setDisplayName('')
      setHandle('')
      setCode('')
      setError('')
      return
    }
    if (open) return
    pendingMember.current = null
    setStep('email')
    setEmail('')
    setChallengeId('')
    setCode('')
    setDisplayName('')
    setHandle('')
    setBusy(false)
    setError('')
    setResendSeconds(0)
  }, [open, resumeProfileMember])

  useEffect(() => {
    if (!open) return
    previouslyFocused.current = document.activeElement instanceof HTMLElement ? document.activeElement : null
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !busyRef.current) {
        closeRef.current()
        return
      }
      if (event.key !== 'Tab') return
      const focusable = [...(dialogRef.current?.querySelectorAll<HTMLElement>('button:not([disabled]), input:not([disabled]), [href], [tabindex]:not([tabindex="-1"])') ?? [])]
        .filter((element) => !element.hasAttribute('hidden'))
      if (!focusable.length) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => {
      window.removeEventListener('keydown', onKeyDown)
      previouslyFocused.current?.focus()
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    const timer = window.setTimeout(() => {
      if (step === 'email') emailInput.current?.focus()
      if (step === 'code') codeInput.current?.focus()
      if (step === 'profile') dialogRef.current?.querySelector<HTMLInputElement>('input')?.focus()
    }, 40)
    return () => window.clearTimeout(timer)
  }, [open, step])

  useEffect(() => {
    if (!open || resendSeconds <= 0) return
    const timer = window.setInterval(() => setResendSeconds((current) => Math.max(0, current - 1)), 1_000)
    return () => window.clearInterval(timer)
  }, [open, resendSeconds])

  if (!open) return null

  async function requestCode(event?: FormEvent) {
    event?.preventDefault()
    if (busy || !email.trim()) return
    setBusy(true)
    setError('')
    try {
      const payload = await authRequest<StartResponse>('/api/auth/email/start', { email: email.trim() })
      setChallengeId(String(payload.challenge_id ?? ''))
      setCode('')
      setResendSeconds(Math.max(30, Number(payload.retryAfterSeconds ?? payload.resend_after_seconds ?? 60)))
      setStep('code')
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'We couldn’t send a code right now. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  async function verifyCode(event?: FormEvent) {
    event?.preventDefault()
    if (busy || code.length !== 6) return
    setBusy(true)
    setError('')
    try {
      const payload = await authRequest<VerifyResponse>('/api/auth/email/verify', {
        email: email.trim(),
        code,
        challenge_id: challengeId,
      })
      if (!payload.member) throw new AuthRequestError('Your account could not be loaded. Please try again.')
      const needsProfile = Boolean(payload.needsProfileSetup ?? payload.needs_profile ?? payload.needsProfile)
      if (needsProfile) {
        pendingMember.current = payload.member
        onProfilePending(payload.member)
        setDisplayName('')
        setHandle('')
        setStep('profile')
      } else {
        onAuthenticated(payload.member)
      }
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'That code doesn’t match. Try again.')
    } finally {
      setBusy(false)
    }
  }

  async function finishProfile(event: FormEvent) {
    event.preventDefault()
    if (busy || !displayName.trim() || handle.length < 3) return
    setBusy(true)
    setError('')
    try {
      const payload = await authRequest<{ member?: AuthMember }>('/api/auth/complete-profile', {
        display_name: displayName.trim(),
        handle,
      })
      const member = payload.member ?? pendingMember.current
      if (!member) throw new AuthRequestError('Your profile could not be loaded. Please try again.')
      onAuthenticated(member)
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Your profile could not be saved.')
    } finally {
      setBusy(false)
    }
  }

  function returnToEmail() {
    if (busy) return
    setStep('email')
    setChallengeId('')
    setCode('')
    setError('')
  }

  const title = step === 'email' ? 'Join the conversation' : step === 'code' ? 'Check your email' : 'Create your fan profile'

  return (
    <div className="auth-backdrop" role="presentation" onMouseDown={(event) => {
      if (event.currentTarget === event.target && !busy) onClose()
    }}>
      <section ref={dialogRef} className="auth-dialog" role="dialog" aria-modal="true" aria-labelledby="auth-title">
        <button className="auth-close" type="button" onClick={onClose} disabled={busy} aria-label="Close sign in">
          <X size={20} />
        </button>

        <div className="auth-brand" aria-hidden="true">
          <span>J</span><b>90</b>
        </div>

        <span className="auth-eyebrow">{step === 'profile' ? 'ONE LAST THING' : 'JOIN JOURNEY90'}</span>
        <h2 id="auth-title">{title}</h2>

        {step === 'email' && (
          <form className="auth-form" onSubmit={requestCode}>
            <p>Enter your email and we’ll send a 6-digit code. No password needed.</p>
            <label className="auth-field">
              <span>Email address</span>
              <span className="auth-field__control"><Mail size={17} /><input ref={emailInput} type="email" autoComplete="email" spellCheck={false} placeholder="you@example.com" value={email} onChange={(event) => setEmail(event.target.value)} required /></span>
            </label>
            <button className="auth-primary" type="submit" disabled={busy || !email.trim()}>
              {busy ? <><LoaderCircle className="auth-spinner" size={17} /> Sending your code…</> : <>Email me a code <Mail size={17} /></>}
            </button>
            <small>Already a member? Use the same flow and we’ll sign you in.</small>
          </form>
        )}

        {step === 'code' && (
          <form className="auth-form" onSubmit={verifyCode}>
            <p>We sent a 6-digit code to <strong>{maskedEmail(email)}</strong>.</p>
            <label className="auth-field auth-field--code">
              <span>Verification code</span>
              <span className="auth-code-wrap"><LockKeyhole size={18} /><input ref={codeInput} type="text" inputMode="numeric" pattern="[0-9]*" autoComplete="one-time-code" maxLength={6} placeholder="000000" value={code} onChange={(event) => setCode(cleanCode(event.target.value))} required /></span>
            </label>
            <button className="auth-primary" type="submit" disabled={busy || code.length !== 6}>
              {busy ? <><LoaderCircle className="auth-spinner" size={17} /> Checking your code…</> : <>Continue <Check size={17} /></>}
            </button>
            <div className="auth-links">
              <button type="button" onClick={returnToEmail}><ArrowLeft size={13} /> Use a different email</button>
              <button type="button" disabled={busy || resendSeconds > 0} onClick={() => void requestCode()}>{resendSeconds > 0 ? `Resend in 0:${String(resendSeconds).padStart(2, '0')}` : 'Resend code'}</button>
            </div>
          </form>
        )}

        {step === 'profile' && (
          <form className="auth-form" onSubmit={finishProfile}>
            <p>{profileIdentitySetupNotice}</p>
            <label className="auth-field">
              <span>Display name</span>
              <span className="auth-field__control"><input type="text" autoComplete="name" maxLength={50} placeholder="90 Day superfan" value={displayName} onChange={(event) => setDisplayName(event.target.value)} required /></span>
            </label>
            <label className="auth-field">
              <span>Handle</span>
              <span className="auth-field__control"><AtSign size={17} /><input type="text" autoComplete="username" minLength={3} maxLength={30} pattern="[a-z0-9][a-z0-9_]{2,29}" placeholder="passportandpopcorn" value={handle} onChange={(event) => setHandle(cleanHandle(event.target.value))} required /></span>
            </label>
            <button className="auth-primary" type="submit" disabled={busy || !displayName.trim() || handle.length < 3}>
              {busy ? <><LoaderCircle className="auth-spinner" size={17} /> Saving your profile…</> : <>Finish joining <Check size={17} /></>}
            </button>
          </form>
        )}

        <div className="auth-error" aria-live="polite">{error}</div>
        <footer>By continuing, you agree to keep Journey90 welcoming, curious, and fun.</footer>
      </section>
    </div>
  )
}
